import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import SundaySchoolLayout from '../SundaySchoolLayout';
import { canEditClass, getSelectedDepartmentId, getSundaySchoolSession } from '../sundaySchoolSession';

type AttStatus = 'ok' | 'late' | 'absent' | 'excused';

type Student = {
  id: number;
  name: string;
  birthdate: string | null;
  registeredAt: string | null;
  tagLabel: string;
  tagDanger: number | boolean;
  status: AttStatus | null;
};

type ClassRow = {
  id: number;
  name: string;
  icon: string;
};

const STATUS_LABEL: Record<AttStatus, string> = {
  ok: '출석',
  late: '지각',
  absent: '결석',
  excused: '공결',
};

const STATUS_CLASS: Record<AttStatus, string> = {
  ok: 'on-ok',
  late: 'on-warn',
  absent: 'on-bad',
  excused: 'on-gray',
};

function ageFrom(birthdate: string | null): string {
  if (!birthdate) return '-';
  const birth = new Date(birthdate);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) {
    age -= 1;
  }
  return `${age}세`;
}

const today = new Date().toISOString().slice(0, 10);

export default function SundaySchoolAttendance() {
  const session = getSundaySchoolSession();
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [activeClass, setActiveClass] = useState(0);
  const [students, setStudents] = useState<Student[]>([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!session) return;
    axios
      .get(`${MainURL}/sundayschool/church`, {
        params: {
          authChurch: session.user.authChurch,
          departmentId: getSelectedDepartmentId(),
          userId: session.user.id,
        },
      })
      .then(({ data }) => {
        if (!data.ok) return;
        const list: ClassRow[] = data.data.classes;
        setClasses(list);
        // 교사는 맡은 반을 먼저 보여 준다.
        const own = list.findIndex((c) => c.id === session.user.classId);
        if (own > 0) setActiveClass(own);
      })
      .catch((err) => console.error('GET /sundayschool/church', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeClassId = classes[activeClass]?.id;
  // 부서담당자 이상은 모든 반, 교사는 맡은 반만 저장할 수 있고 스텝은 보기만 한다.
  const editable = canEditClass(session, activeClassId);

  useEffect(() => {
    if (!activeClassId || !session) return;
    axios
      .get(`${MainURL}/sundayschool/attendance`, {
        params: {
          authChurch: session.user.authChurch,
          departmentId: getSelectedDepartmentId(),
          userId: session.user.id,
          classId: activeClassId,
          date: today,
        },
      })
      .then(({ data }) => {
        if (data.ok) setStudents(data.data);
      })
      .catch((err) => console.error('GET /sundayschool/attendance', err));
    setSaved(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClassId]);

  const counts = useMemo(() => {
    const c = { ok: 0, late: 0, absent: 0, excused: 0 };
    students.forEach((s) => {
      if (s.status) c[s.status]++;
    });
    return c;
  }, [students]);

  const setStatus = (id: number, status: AttStatus) => {
    setStudents((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
    setSaved(false);
  };

  const onSave = async () => {
    if (!activeClassId || !session) return;
    const records = students.filter((s) => s.status).map((s) => ({ studentId: s.id, status: s.status }));
    try {
      const { data } = await axios.post(`${MainURL}/sundayschool/attendance`, {
        authChurch: session.user.authChurch,
        departmentId: getSelectedDepartmentId(),
        userId: session.user.id,
        classId: activeClassId,
        date: today,
        records,
      });
      if (data.ok) setSaved(true);
    } catch (err) {
      console.error('POST /sundayschool/attendance', err);
    }
  };

  return (
    <SundaySchoolLayout footNote="출석부 관리 · 체크 즉시 학부모 푸시 연동">
      <div className="admin-attendance">
        <div className="page-head">
          <div>
            <h1>오늘의 출석 체크 ✅</h1>
            <div className="sub">{today} · 저장하면 학부모 푸시가 자동 전송돼요</div>
          </div>
          <div className="flex">
            <span className="badge badge-coral">
              체크 {students.filter((s) => s.status).length} / {students.length}
            </span>
          </div>
        </div>

        <div className="tabs">
          {classes.map((c, i) => (
            <button key={c.id} className={`tab${activeClass === i ? ' on' : ''}`} onClick={() => setActiveClass(i)}>
              {c.icon} {c.name}
            </button>
          ))}
        </div>

        <div className="notice-bar">
          📢 저장 전입니다 —{' '}
          <b>
            출석 {counts.ok} · 지각 {counts.late} · 결석 {counts.absent}
          </b>{' '}
          · 저장하면 학부모에게 도착 알림이 전송됩니다
        </div>

        <div className="card">
          <div className="tbl-wrap">
            {students.length > 0 ? (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>학생</th>
                    <th>나이</th>
                    <th>특이사항</th>
                    <th className="col-status">출석 상태</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <div className="name-cell">
                          <span className="avatar av-1">{s.name.slice(0, 1)}</span>
                          <div>
                            {s.name}
                            <div className="s muted student-info">
                              {ageFrom(s.birthdate)} · {s.registeredAt ?? '-'} 등록
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>{ageFrom(s.birthdate)}</td>
                      <td>
                        {s.tagLabel && (
                          <span className={`chip${s.tagDanger ? ' badge-danger' : ''}`}>{s.tagLabel}</span>
                        )}
                      </td>
                      <td>
                        <div className="att-check">
                          {(Object.keys(STATUS_LABEL) as AttStatus[]).map((v) => (
                            <button
                              key={v}
                              className={s.status === v ? STATUS_CLASS[v] : ''}
                              onClick={() => setStatus(s.id, v)}
                              disabled={!editable}
                            >
                              {STATUS_LABEL[v]}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="tbl">
                <tbody>
                  <tr>
                    <td className="placeholder-cell muted">{classes[activeClass]?.name} 명단이 비어 있습니다</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>

        {students.length > 0 && !editable && (
          <div className="banner mt16">
            <span className="b-emoji">👀</span>
            <div className="grow">
              <strong>보기 전용</strong>
              <div className="mt8 save-banner-note">맡은 반의 출석만 체크할 수 있어요. (스텝은 보기만 가능)</div>
            </div>
          </div>
        )}
        {students.length > 0 && editable && (
          <div className="banner sage mt16">
            <span className="b-emoji">🔔</span>
            <div className="grow">
              <strong>저장하면 학부모에게 도착 알림이 가요</strong>
              <div className="mt8 save-banner-note">
                결석한 아이의 가족에게는 별도의 안내 문구가 함께 발송됩니다. (결석 사유 확인)
              </div>
            </div>
            <button className="btn btn-primary" onClick={onSave}>
              {saved ? '✅ 저장 완료! 학부모 알림 발송됨' : '저장하기'}
            </button>
          </div>
        )}
      </div>
    </SundaySchoolLayout>
  );
}
